/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  forwardRef,
  Fragment,
  useMemo,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import { Checkbox, DatePicker, Popover, Radio } from 'antd';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './MovementsBlock.style';
import { TenderTabsProps } from '~components/molecules/Tabs/TenderTabs/TenderTabs';
import { TenderMovementsTable } from '~components/atoms/FileTables/TenderMovements/TenderMovementsTable';
import { Input, Select, Typography } from 'antd';
import { useQuery } from 'react-query';
import {
  getMovementsData,
  getMovementsDataVendors,
} from '~src/services/api/movements';
import {
  getReadableType,
  groupedTypeMap,
  getParentCheckboxState,
  getGlobalCheckboxState,
  toggleParentGroup,
  toggleChildItem,
  toggleAllItems,
  groupNameIntlKeys,
} from '~src/utils/movementsUtils/movementsUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconButton from '~components/_common/IconButton/IconButton';
import { IntlShape, useIntl } from 'react-intl';
import { CheckOutlined } from '@ant-design/icons';
import { TableRowSelection } from 'antd/lib/table/interface';
import { TableEntry } from '~components/atoms/FileTables/FileTable.utils';

const { RangePicker } = DatePicker;

type MovementsBlockProps = Pick<
  TenderTabsProps,
  'highlightedRow' | 'focusOptions' | 'scroll'
> & {
  tenderId?: string;
  projectId?: number | undefined;
  tenderRegData?: any;
  isStartFetch?: boolean;
  onSelect?: TableRowSelection<TableEntry>['onSelect'];
  onSelectAll?: TableRowSelection<TableEntry>['onSelectAll'];
  setSelectedItems?: Dispatch<SetStateAction<TableEntry[]>>;
  selectedKeys?: TableRowSelection<TableEntry>['selectedRowKeys'];
  bottomViewHeight?: number;
  shareId?: string;
  shareKey?: string;
};

type LangCode = 'da' | 'en' | 'de' | 'sv';

const LANG_OPTIONS: Array<{ value: LangCode; label: string }> = [
  { value: 'da', label: 'DA' },
  { value: 'en', label: 'EN' },
  { value: 'de', label: 'DE' },
  { value: 'sv', label: 'SV' },
];

const normalizeLang = (raw?: string): LangCode => {
  const short = String(raw || '')
    .trim()
    .toLowerCase()
    .slice(0, 2);

  if (short === 'da' || short === 'en' || short === 'de' || short === 'sv') {
    return short;
  }
  return 'en';
};

// Helper function to create unique IDs by combining movement id with custom created_at
const createUniqueMovements = (movements: any[]) => {
  return movements.map((movement) => ({
    ...movement,
    // Store original ID for display
    originalId: movement.id,
    // Create unique ID by combining original id with custom created_at timestamp
    id: movement.custom?.created_at
      ? `${movement.id}_${movement.custom.created_at}`
      : movement.id,
  }));
};

// Helper function to search across multiple fields
const searchInMovement = (
  movement: any,
  searchTerm: string,
  intl: any
): boolean => {
  if (!searchTerm) return true;

  const searchLower = searchTerm.toLowerCase();

  // Search in basic fields
  const basicFieldsMatch = [
    movement.created_by_user_name,
    getReadableType(movement.type, intl),
    movement.custom?.note,
    movement.custom?.created_by_user_name,
    movement.summary?.head,
  ].some((field) => field?.toLowerCase().includes(searchLower));

  // Search in target entities
  const targetEntitiesMatch = movement.target_entities?.some((entity: any) =>
    [entity.type, entity.display_name, entity.description].some((field) =>
      field?.toLowerCase().includes(searchLower)
    )
  );

  // Search in changes
  const changesMatch = movement.changes?.some((change: any) =>
    [change.field, change.old_value, change.new_value].some((field) =>
      field?.toLowerCase().includes(searchLower)
    )
  );

  // Search in summary actions
  const summaryActionsMatch = movement.summary?.actions?.some(
    (action: string) => action?.toLowerCase().includes(searchLower)
  );

  return (
    basicFieldsMatch ||
    targetEntitiesMatch ||
    changesMatch ||
    summaryActionsMatch
  );
};

type ChangesMode = 1 | 2; // 1 = all, 2 = latest

const isLatestMovement = (movement: any): boolean => {
  // "Latest changes" = not ignored AND not already included in a sent report.
  // Current report (draft) must NOT affect the result.
  return (
    movement?.report_data?.is_ignore === false &&
    movement?.report_data?.is_report_sent === false
  );
};

export const MovementsBlock = forwardRef<HTMLDivElement, MovementsBlockProps>(
  (
    {
      tenderId,
      projectId,
      tenderRegData,
      highlightedRow,
      focusOptions,
      isStartFetch,
      setSelectedItems,
      onSelect,
      onSelectAll,
      selectedKeys,
      scroll,
      bottomViewHeight = 0,
      shareId,
      shareKey,
      ...tableProps
    },
    ref
  ) => {
    const intl = useIntl();
    const styles = useStyles(styleFn);
    const isOnTender = !!shareId && !!shareKey;

    // Movements API supports ?lang=da|en|de|sv
    const defaultLang = useMemo<LangCode>(() => {
      return normalizeLang((intl as any)?.locale);
    }, [intl]);

    const [lang, setLang] = useState<LangCode>(defaultLang);

    // If locale changes (language switch), keep movements reader in sync
    useEffect(() => {
      setLang(defaultLang);
    }, [defaultLang]);

    const [filterSearch, setFilterSearch] = useState('');
    const [filterTypes, setFilterTypes] = useState<number[]>([]);
    const [filterDateRange, setFilterDateRange] = useState<
      [number | null, number | null]
    >([null, null]);

    const [changesMode, setChangesMode] = useState<ChangesMode>(2);
    const [showIgnored, setShowIgnored] = useState(true);

    /* JIRA: MOVEMENTS tab commenting out  */
    // Changed default to true so filters load by default
    const [filterShow, setFilterShow] = useState<boolean>(true);

    // Reset all filters when filterShow is toggled off
    useEffect(() => {
      if (!filterShow) {
        setFilterSearch('');
        setFilterTypes([]);
        setFilterDateRange([null, null]);
      } else {
        // When filters are shown, by default select all types
        setAllTypesSelected();
      }
    }, [filterShow]);

    const setAllTypesSelected = () => {
      const allIds = Object.values(groupedTypeMap)
        .flat()
        .map((i) => i.id);
      setFilterTypes(allIds);

      const allGrouped: Record<string, number[]> = {};
      Object.entries(groupedTypeMap).forEach(([groupName, items]) => {
        allGrouped[groupName] = items.map((i) => i.id);
      });
      setSelectedValues(allGrouped);
    };

    useEffect(() => {
      // Синхронізуй selectedValues з filterTypes
      const updated: Record<string, number[]> = {};
      Object.entries(groupedTypeMap).forEach(([groupName, items]) => {
        const selected = items
          .filter((i) => filterTypes.includes(i.id))
          .map((i) => i.id);
        if (selected.length > 0) updated[groupName] = selected;
      });
      setSelectedValues(updated);
    }, [filterTypes]);

    const [selectedValues, setSelectedValues] = useState<
      Record<string, number[]>
    >({});

    const movementTypeData = Object.entries(groupedTypeMap).map(
      ([groupName, items], idx) => ({
        id: idx + 1,
        name: groupName,
        title: intl.formatMessage({
          id: groupNameIntlKeys[groupName] || groupName,
        }),
        labels: items.map((i) => ({
          id: i.id,
          title: intl.formatMessage({ id: i.label }),
        })),
      })
    );

    const handleGroupSelect = (groupKey: string, groupOptions: any[]) => {
      const allSelected = groupOptions.every((option) =>
        selectedValues[groupKey]?.includes(option.id)
      );
      const newValues = allSelected
        ? []
        : groupOptions.map((option) => option.id);

      setSelectedValues((prev) => {
        const updated = { ...prev, [groupKey]: newValues };
        const combined = Object.values(updated).flat();
        setFilterTypes(combined);
        return updated;
      });
    };

    const handleSingleSelect = (groupKey: string, optionKey: number) => {
      const isSelected = selectedValues[groupKey]?.includes(optionKey) || false;
      const updated = {
        ...selectedValues,
        [groupKey]: isSelected
          ? selectedValues[groupKey].filter((id) => id !== optionKey)
          : [...(selectedValues[groupKey] || []), optionKey],
      };
      setSelectedValues(updated);
      const combined = Object.values(updated).flat();
      setFilterTypes(combined);
    };

    const isLatestMode = changesMode === 2;
    // Vendor side already filters latest via query params (and report_data can be null)
    const shouldApplyLatestFilterFrontend = !(isOnTender && isLatestMode);

    const { data: movementsData = [], isLoading, isError } = useQuery(
      ['movements', projectId, tenderId, shareId, shareKey, lang, changesMode],
      () => {
        if (isOnTender) {
          return getMovementsDataVendors({
            shareId: String(shareId),
            shareKey: String(shareKey),
            lang,
            ...(isLatestMode
              ? {
                  is_report_sent: false,
                  is_ignore: false,
                }
              : {}),
          } as any);
        }

        return getMovementsData({
          project_id: Number(projectId),
          tender_id: Number(tenderId),
          lang,
          ...(isLatestMode
            ? {
                is_report_sent: false,
                is_ignore: false,
              }
            : {}),
        } as any);
      },
      {
        enabled: isOnTender
          ? !!shareId && !!shareKey
          : !!projectId && !!tenderId,
      }
    );

    const filteredData = useMemo(() => {
      // First, create unique IDs for movements
      const uniqueMovements = createUniqueMovements(movementsData);

      // Then apply filters
      return uniqueMovements.filter((item) => {
        const latestMatch = !isLatestMode
          ? true
          : shouldApplyLatestFilterFrontend
          ? isLatestMovement(item)
          : true;

        const searchMatch = searchInMovement(item, filterSearch, intl);

        const typeMatch =
          filterTypes.length > 0 ? filterTypes.includes(item.type) : true;

        const dateMatch =
          filterDateRange[0] && filterDateRange[1]
            ? item.created_at >= filterDateRange[0] &&
              item.created_at <= filterDateRange[1]
            : true;

        const ignoredMatch = showIgnored
          ? true
          : item?.report_data?.is_ignore !== true;

        return (
          latestMatch && searchMatch && typeMatch && dateMatch && ignoredMatch
        );
      });
    }, [
      movementsData,
      filterSearch,
      filterTypes,
      filterDateRange,
      isLatestMode,
      showIgnored,
    ]);

    const [dropdownOpen, setDropdownOpen] = useState(false);

    return (
      <Fragment>
        <div css={styles.filterBlock}>
          {/*Title */}
          {/* JIRA: MOVEMENTS tab commenting out Commented out Filter Toggle */}
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '5px',
            }}>
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexDirection: 'column',
              }}>
              <Radio.Group
                value={changesMode}
                onChange={(e) => setChangesMode(e.target.value)}
                css={{ display: 'flex', flexDirection: 'column' }}>
                <Radio value={2} css={{ whiteSpace: 'nowrap' }}>
                  {intl.formatMessage({ id: 'show-latest-changes' })}
                </Radio>
                <Radio value={1} css={{ whiteSpace: 'nowrap' }}>
                  {intl.formatMessage({ id: 'show-all-changes' })}
                </Radio>
              </Radio.Group>
            </div>
            {/*<div*/}
            {/*  css={{*/}
            {/*    display: 'flex',*/}
            {/*    whiteSpace: 'nowrap',*/}
            {/*    justifySelf: 'flex-start',*/}
            {/*    gap: '5px',*/}
            {/*    cursor: 'pointer',*/}
            {/*  }}*/}
            {/*  onClick={() => setFilterShow(!filterShow)}>*/}
            {/*  <div*/}
            {/*    css={{*/}
            {/*      display: 'flex',*/}
            {/*      justifyContent: 'center',*/}
            {/*      alignItems: 'center',*/}
            {/*      gap: '5px',*/}
            {/*    }}>*/}
            {/*    <Checkbox*/}
            {/*      checked={filterShow}*/}
            {/*      onChange={(e) => setFilterShow(e.target.checked)}*/}
            {/*    />*/}
            {/*  </div>*/}
            {/*  <span css={{ userSelect: 'none' }}>*/}
            {/*    {intl.formatMessage({ id: 'filters' })}*/}
            {/*  </span>*/}
            {/*</div>*/}
          </div>
          {filterShow && (
            <Fragment>
              {/*TimeStamp */}
              <div css={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {intl.formatMessage({ id: 'timestamp' })}
                <div
                  css={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <RangePicker
                    showTime
                    format="DD/MM/YYYY HH:mm"
                    onChange={(dates) => {
                      setFilterDateRange([
                        dates?.[0]?.unix() ?? null,
                        dates?.[1]?.unix() ?? null,
                      ]);
                    }}
                    css={{ minWidth: '300px', maxWidth: '420px' }}
                  />
                </div>
              </div>

              {/*Search */}
              <div css={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div>
                  <Input
                    type="text"
                    placeholder={`${intl.formatMessage({ id: 'search' })}`}
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    prefix={<FontAwesomeIcon icon={['far', 'search']} />}
                    css={{ width: '200px' }}
                  />
                </div>
              </div>
              {/*Type */}
              <Select
                className="borderless"
                style={{
                  width: '100%',
                  maxWidth: '280px',
                  paddingRight: '20px',
                }}
                mode="tags"
                open={dropdownOpen}
                onDropdownVisibleChange={(open) => setDropdownOpen(open)}
                placeholder={
                  <Fragment>
                    <IconButton
                      iconStyle="far"
                      icon="filter-list"
                      customStyle={{ marginRight: '5px' }}
                      backgroundOff
                    />
                    <span>
                      {intl.formatMessage({
                        id: 'movements-choose-type',
                      })}
                    </span>
                  </Fragment>
                }
                value={[]}
                dropdownRender={() => (
                  <div
                    style={{
                      maxHeight: '400px',
                      overflowY: 'auto',
                      padding: '6px 10px',
                    }}>
                    {movementTypeData.map((group) => (
                      <div className="group-item" key={group.name}>
                        <div
                          className="group-label"
                          style={
                            group.labels.every((option) =>
                              selectedValues[group.name]?.includes(option.id)
                            )
                              ? {
                                  background: '#e6f7ff',
                                  fontWeight: 600,
                                  padding: '2px 6px',
                                }
                              : { padding: '2px 6px' }
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleGroupSelect(group.name, group.labels);
                          }}>
                          {group.title}
                          {group.labels.every((option) =>
                            selectedValues[group.name]?.includes(option.id)
                          ) && <CheckOutlined rev={undefined} />}
                        </div>
                        <div className="sub-options">
                          {group.labels.map(({ title, id }) => (
                            <div
                              className="sub-option"
                              key={id}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSingleSelect(group.name, id);
                              }}>
                              {title}
                              {selectedValues[group.name]?.includes(id) && (
                                <CheckOutlined rev={undefined} />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              />
              {/*Language */}
              <div css={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/*{intl.formatMessage({*/}
                {/*  id: 'movements-lang-label',*/}
                {/*  defaultMessage: 'Language',*/}
                {/*})}*/}
                <Select
                  value={lang}
                  onChange={(v: LangCode) => setLang(v)}
                  options={LANG_OPTIONS}
                  style={{ width: 110 }}
                />
              </div>
              <Checkbox
                checked={isLatestMode ? false : showIgnored}
                disabled={isLatestMode}
                onChange={(e) => setShowIgnored(e.target.checked)}
                css={{ whiteSpace: 'nowrap' }}>
                {intl.formatMessage({ id: 'show-ignored' })}
              </Checkbox>
            </Fragment>
          )}
        </div>
        <div css={[styles.blockContainer]}>
          <TenderMovementsTable
            {...tableProps}
            tenderId={tenderId}
            projectId={projectId}
            movementsList={filteredData}
            isLoading={isLoading}
            highlightedRow={highlightedRow}
            scroll={{ y: `calc(100vh - 250px - ${bottomViewHeight}px)` }}
            tenderRegData={tenderRegData}
            setSelectedItems={setSelectedItems}
            onSelect={onSelect}
            onSelectAll={onSelectAll}
            selectedKeys={selectedKeys}
            isOnMyTender={isOnTender}
          />
          {/* PRINT ONLY COMPONENT */}
          <div css={styles.printContainer}>
            <TenderMovementsTable
              {...tableProps}
              tenderId={tenderId}
              projectId={projectId}
              movementsList={filteredData}
              isLoading={isLoading}
              css={styles.printTable}
              ref={ref}
              isStartFetch={true}
              highlightedRow={highlightedRow}
              tenderRegData={tenderRegData}
              setSelectedItems={setSelectedItems}
            />
          </div>
        </div>
      </Fragment>
    );
  }
);

export default MovementsBlock;
